import React from "react";

import { faNotdef, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "src/base-components/Icon";
import { type Option, Select } from "src/base-components/Select";
import { TYPE_ICONS } from "src/utils/constants";
import { assertUnreachable } from "src/utils/typeUtils";

export type SchemaType =
  | "string"
  | "boolean"
  | "number"
  | "integer"
  | "object"
  | "list"
  | "datetime"
  | "date"
  | "enum"
  | "tags"
  | "address"
  | "ip_address"
  | "lat_long"
  | "url"
  | "file"
  | "array";

const BaseSchemaTypes: SchemaType[] = [
  "string",
  "boolean",
  "number",
  "integer",
  "object",
  "list",
  "datetime",
  "date",
  "enum",
  "tags",
  "address",
  "ip_address",
  "lat_long",
  "url",
  "file",
  "array",
] as const;

export const isBaseSchemaType = (type: string): type is SchemaType => {
  return BaseSchemaTypes.includes(type as SchemaType);
};

export const getSchemaTypeIcon = (schemaType: SchemaType) => {
  switch (schemaType) {
    case "string":
      return TYPE_ICONS.string;
    case "boolean":
      return TYPE_ICONS.boolean;
    case "number":
      return TYPE_ICONS.number;
    case "integer":
      return TYPE_ICONS.integer;
    case "object":
      return TYPE_ICONS.object;
    case "list":
      return TYPE_ICONS.tags;
    case "datetime":
      return TYPE_ICONS.datetime;
    case "date":
      return TYPE_ICONS.date;
    case "enum":
      return TYPE_ICONS.enum;
    case "tags":
      return TYPE_ICONS.tag;
    case "address":
      return TYPE_ICONS.address;
    case "ip_address":
      return TYPE_ICONS.ipAddress;
    case "lat_long":
      return TYPE_ICONS.latLong;
    case "url":
      return TYPE_ICONS.url;
    case "file":
      return TYPE_ICONS.object;
    case "array":
      return TYPE_ICONS.array;
    default:
      assertUnreachable(schemaType);
      return faNotdef;
  }
};

const isIconDefinition = (icon: unknown): icon is IconDefinition => {
  return (
    typeof icon === "object" &&
    icon !== null &&
    "icon" in icon &&
    "prefix" in icon &&
    "iconName" in icon
  );
};

export const TypeOption = <T extends string>({
  label,
  icon: iconProp,
}: {
  label: SchemaType | T;
  icon?: IconDefinition | React.ReactNode;
}) => {
  const icon =
    iconProp ?? (isBaseSchemaType(label) ? getSchemaTypeIcon(label) : faNotdef);
  return (
    <div className="flex items-center gap-x-1.5">
      {isIconDefinition(icon) ? <Icon icon={icon} size="xs" /> : icon}
      <div className="truncate">{label}</div>
    </div>
  );
};

export const SCHEMA_TYPE_OPTIONS = BaseSchemaTypes.map((type) => ({
  key: type,
  value: <TypeOption label={type} />,
}));

type SchemaTypeSelectorProps<T extends string = SchemaType> = {
  placeholder?: string;
  value: T | undefined;
  disabled?: boolean;
  fullWidth?: boolean;
  onChange?: (value: string) => void;
  options?: Option[];
  dataLoc?: string;
  isLoading?: boolean;
  errored?: boolean;
  errorTooltip?: React.ReactNode;
};

export const SchemaTypeSelector = <T extends string = SchemaType>({
  placeholder = "Select type",
  value,
  disabled,
  fullWidth,
  onChange,
  options = SCHEMA_TYPE_OPTIONS,
  dataLoc,
  isLoading,
  errored,
  errorTooltip,
}: SchemaTypeSelectorProps<T>) => {
  return (
    <Select
      dataLoc={dataLoc}
      disabled={disabled}
      errorTooltip={errorTooltip}
      errored={errored}
      fullWidth={fullWidth}
      loading={isLoading}
      options={options}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  );
};
